import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  GetAnalysisPipelineStepDefinition,
} from 'analysis-tool/AnalysisTypes';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Optional} from 'util/Types';

import {AnalysisRightSidebarItem} from '../../right-sidebar/RightSidebarTypes';

// When adding a new AnalysisMainViewItemType, make sure to add it to AnalysisMainViewItemType, and one of either
// STEP_INITIALIZATION_ITEM_TYPES or STEP_AWARE_ANALYSIS_ITEM_TYPES, if necessary.
export enum AnalysisMainViewItemType {
  CHOOSE_DATASET_CREATION_METHOD = 'CHOOSE_DATASET_CREATION_METHOD',
  COMBINE_DATA_FRAMES = 'COMBINE_DATA_FRAMES',
  CREATE_OUTPUTS = 'CREATE_OUTPUTS',
  DATA_CATALOG_DATASET_CONFIGURATION = 'DATA_CATALOG_DATASET_CONFIGURATION',
  DATA_LAYOUT = 'DATA_LAYOUT',
  DATA_VIEW = 'DATA_VIEW',
  EMPTY_ANALYSIS = 'EMPTY_ANALYSIS',
  EXPORT_DATA_CATALOG_DATASET_OUTPUT = 'EXPORT_DATA_CATALOG_DATASET_OUTPUT',
  FAILED = 'FAILED',
  HALTED = 'HALTED',
  FLOWCHART = 'FLOWCHART',
  INSIGHTS_BLOCK_DATASET_CONFIGURATION = 'INSIGHTS_BLOCK_DATASET_CONFIGURATION',
  LOOK_UP = 'LOOK_UP',
  PLATE_SCHEMA_DATASET_CONFIGURATION = 'PLATE_SCHEMA_DATASET_CONFIGURATION',
  REGISTRY_SCHEMA_DATASET_CONFIGURATION = 'REGISTRY_SCHEMA_DATASET_CONFIGURATION',
  UPLOAD_CSV_DATASET_CONFIGURATION = 'UPLOAD_CSV_DATASET_CONFIGURATION',
  RUNNING = 'RUNNING',
  SAVE_TO_RESULTS_CONFIGURATION = 'SAVE_TO_RESULTS_CONFIGURATION',
  SAVE_TO_RESULTS_OUTPUT = 'SAVE_TO_RESULTS_OUTPUT',
  SAVE_TO_RESULTS_RECONFIGURATION = 'SAVE_TO_RESULTS_RECONFIGURATION',
  SEND_TO_DOCUMENT = 'SEND_TO_DOCUMENT',
  TEMPLATE_APPLY = 'TEMPLATE_APPLY',
  TEMPLATE_CONFIGURATION = 'TEMPLATE_CONFIGURATION',
  REGRESSION = 'REGRESSION',
  EDIT_REGRESSION = 'EDIT_REGRESSION',
}

export interface DataReference {
  stepId: string;
  stepDataName: string;
}

export interface CreationOptionalIds {
  stepIdToReplace?: string;
}

export interface ViewReference {
  stepId: string;
  outputReference: {
    stepDataName: string;
    rawDataIndex: number | null;
  } | null;
}

/**
 * TODO(BNCH-60583): Currently there is inconsistent naming for the views to (1) initially configure a step,
 * (2) view the configuration of a completed step, and (3) re-configure a step. See
 * `INSIGHTS_BLOCK_DATASET_CONFIGURATION` versus `SAVE_TO_RESULTS_CONFIGURATION`. Standardize the terminology.
 */
export type AnalysisMainViewItem<T extends AnalysisMainViewItemType = AnalysisMainViewItemType> = {
  type: T;
} & (
  | ({type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.COMBINE_DATA_FRAMES} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.CREATE_OUTPUTS}
  | ({type: AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.DATA_LAYOUT; stepId: string}
  // isUpdating is in the MainViewItem because we have to update it at the same time as the new viewReference
  // or there might be race conditions with out of sync analyses and viewReferences.
  | {
      type: AnalysisMainViewItemType.DATA_VIEW;
      viewReference: ViewReference;
      isUpdating?: true;
      showChips?: boolean | null;
    }
  | {type: AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT; stepId: string; dataFrameId: string}
  | {type: AnalysisMainViewItemType.FAILED; stepId: string}
  | {type: AnalysisMainViewItemType.HALTED; stepId: string}
  | {type: AnalysisMainViewItemType.EMPTY_ANALYSIS}
  | {type: AnalysisMainViewItemType.FLOWCHART; highlightedStepIds?: Array<string>}
  | ({type: AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.LOOK_UP}
  | ({type: AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION} & CreationOptionalIds)
  | ({type: AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION} & CreationOptionalIds)
  | {type: AnalysisMainViewItemType.RUNNING; stepId: string}
  | {
      type: AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION;
      inputDataReference: DataReference;
      inputDataFrameId: string;
    }
  | {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT; stepId: string}
  | {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION; stepId: string}
  | {type: AnalysisMainViewItemType.SEND_TO_DOCUMENT; stepId: string}
  | {type: AnalysisMainViewItemType.TEMPLATE_APPLY}
  | {type: AnalysisMainViewItemType.TEMPLATE_CONFIGURATION}
  | {type: AnalysisMainViewItemType.REGRESSION; stepId: string}
  | {type: AnalysisMainViewItemType.EDIT_REGRESSION; stepId: string}
);

/**
 * These view item types are views that perform configuration for the addition of step types. For example, data
 * set creation needs to be configured to specify how to create the dataset. These should not be step-aware since
 * they're always opened before adding a step.
 *
 * @package
 */
export const STEP_INITIALIZATION_ITEM_TYPES = [
  AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD,
  AnalysisMainViewItemType.COMBINE_DATA_FRAMES,
  AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.LOOK_UP,
  AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION,
] as const;

export type StepInitializationAnalysisMainViewItemType = (typeof STEP_INITIALIZATION_ITEM_TYPES)[number];

export type StepInitializationAnalysisMainViewItem = AnalysisMainViewItem & {
  type: StepInitializationAnalysisMainViewItemType;
};

export const VIEW_STEP_CONFIG_ITEM_TYPES = [
  AnalysisMainViewItemType.EDIT_REGRESSION,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
] as const;

export type ViewStepConfigAnalysisMainViewItemType = (typeof VIEW_STEP_CONFIG_ITEM_TYPES)[number];

export const STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE: {
  [key in PipelineStepType]?: ViewStepConfigAnalysisMainViewItemType;
} = {
  [PipelineStepType.REGRESSION]: AnalysisMainViewItemType.EDIT_REGRESSION,
  [PipelineStepType.SAVE_TO_RESULTS]: AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
};

/**
 * These item types are related to a specific pipeline step and should therefore include stepId in their
 * corresponding AnalysisMainViewItem interfaces. MainView components that are step-aware will be passed the
 * GraphQL step object corresponding to the stepId defined in the AnalysisMainViewItem object.
 *
 * @package
 */
export const STEP_AWARE_ITEM_TYPES = [
  AnalysisMainViewItemType.DATA_LAYOUT,
  AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT,
  AnalysisMainViewItemType.FAILED,
  AnalysisMainViewItemType.HALTED,
  AnalysisMainViewItemType.RUNNING,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT,
  AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION,
  AnalysisMainViewItemType.SEND_TO_DOCUMENT,
  AnalysisMainViewItemType.REGRESSION,
  AnalysisMainViewItemType.EDIT_REGRESSION,
] as const;

export type StepAwareAnalysisMainViewItemType = (typeof STEP_AWARE_ITEM_TYPES)[number];

/**
 * Utility type to narrow down `AnalysisMainViewItem` to a specific subtype based on the `type` key.
 */
export type GenericAnalysisMainViewItem<T extends AnalysisMainViewItemType> = Extract<
  AnalysisMainViewItem,
  {type: T}
>;

export function isStepInitializationAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<StepInitializationAnalysisMainViewItemType> {
  return STEP_INITIALIZATION_ITEM_TYPES.includes(item.type as StepInitializationAnalysisMainViewItemType);
}

export function isViewStepConfigAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<ViewStepConfigAnalysisMainViewItemType> {
  return VIEW_STEP_CONFIG_ITEM_TYPES.includes(item.type as ViewStepConfigAnalysisMainViewItemType);
}

export function isViewStepConfigEnabledForPipelineStepDefinition(
  definition: GetAnalysisPipelineStepDefinition
): boolean {
  // A `SAVE_TO_RESULTS` step can only be reconfigured if no Results were created;
  // `AnalysisMainViewItemType.SAVE_TO_RESULTS_RECONFIGURATION` is accessed from within the step output view.
  if (definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
    return false;
  }
  return STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE[definition.stepType] != null;
}

export function isStepAwareAnalysisItem(
  item: AnalysisMainViewItem
): item is GenericAnalysisMainViewItem<StepAwareAnalysisMainViewItemType> {
  return STEP_AWARE_ITEM_TYPES.includes(item.type as StepAwareAnalysisMainViewItemType);
}

// This will error if you have not added stepId to the AnalysisMainViewItemUnion interface for any step-aware
// main view item types.
export type _AnalysisMainViewItemsHaveStepIdsCheck =
  GenericAnalysisMainViewItem<StepAwareAnalysisMainViewItemType>['stepId'];

type StepAwareMainViewComponentProps<T extends AnalysisMainViewItemType> =
  T extends StepAwareAnalysisMainViewItemType
    ? {
        /** If T is step-aware, the MainView component is passed the pipeline step GraphQL object. */
        step: GetAnalysisPipelineStep;
        /** The analysis-level clientStateId, before the per-step suffix is appended. */
        analysisClientStateId: string;
      }
    : {};

export type MainViewComponentProps<T extends AnalysisMainViewItemType> =
  StepAwareMainViewComponentProps<T> & {
    analysis: AnalysisGraphQL | AnalysisTemplateGraphQL;
    clientStateId: string;
    selectedItem: GenericAnalysisMainViewItem<T>;
    onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
    onChangeSelectedRightSidebarItem: (newSelectedItem: AnalysisRightSidebarItem | null) => void;

    /** Opens step initialization page and closes the sidebar */
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
    /** Closes step initialization page and re-opens the sidebar */
    onCloseStepInitializationView: (newSelectedItem?: Optional<AnalysisMainViewItem>) => void;
  };
